/**
 * Admin Orders List API Contract
 *
 * Wire format for:
 *   GET /api/v1/admin/orders  (ticketing-service AdminOrdersController)
 *
 * Shared between ticketing-service (producer) and admin frontend (consumer).
 * Both must import from here rather than defining local interfaces.
 */

import { z } from 'zod';

import { PaymentGatewaySchema } from '../payment';
import { TicketCategorySchema } from '../ticketing';

export const AdminOrderItemSchema = z.object({
  id: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  quantity: z.number().int(),
  unitPrice: z.number(),
  subtotal: z.number(),
  category: TicketCategorySchema.optional(),
});

export type AdminOrderItem = z.infer<typeof AdminOrderItemSchema>;

export const AdminOrderSummarySchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  orderType: z.string().optional(),
  groupBookingNumber: z.string().nullable().optional(),
  customerName: z.string(),
  customerEmail: z.string(),
  customerPhone: z.string().nullable().optional(),
  orderDate: z.string(),
  visitDate: z.string(),
  // Full set of order lifecycle statuses the admin API can return. The
  // ticketing-service uppercases the persisted (lowercase) status before
  // serialising, so every DB status maps to one of these. Keep in sync with
  // the order_projections / orders status values across services.
  status: z.enum([
    'PENDING',
    'PENDING_PAYMENT',
    'CONFIRMED',
    'PAID',
    'PROCESSING',
    'FULFILLED',
    'COMPLETED',
    'PARTIALLY_USED',
    'PAYMENT_FAILED',
    'CANCELLED',
    'REFUNDED',
    'EXPIRED',
    'FULFILLMENT_FAILED',
  ]),
  total: z.number(),
  paymentMethod: z.string(),
  ticketCount: z.number().int(),
  items: z.array(AdminOrderItemSchema).optional(),
});

export type AdminOrderSummary = z.infer<typeof AdminOrderSummarySchema>;

export const AdminOrdersListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(25),
  sortBy: z
    .enum(['orderDate', 'visitDate', 'total', 'status', 'orderNumber'])
    .default('orderDate'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  status: z.string().optional(),
  paymentMethod: z.string().optional(),
  search: z.string().optional(),
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export type AdminOrdersListQuery = z.infer<typeof AdminOrdersListQuerySchema>;

export const AdminOrdersListResponseSchema = z.object({
  orders: z.array(AdminOrderSummarySchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

export type AdminOrdersListResponse = z.infer<typeof AdminOrdersListResponseSchema>;

// ============================================================================
// Order Detail
// ============================================================================

export const AdminOrderTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: TicketCategorySchema.optional(),
  status: z.enum(['PENDING', 'ACTIVE', 'USED', 'EXPIRED', 'CANCELLED', 'REFUNDED', 'VOIDED']),
  qrCodeToken: z.string(),
  visitorName: z.string(),
  visitorEmail: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  /**
   * When the ticket was first validated at a gate.
   *
   * Named `usedAt` because that is what the endpoint actually sends
   * (`admin-orders.service.ts` maps `Ticket.firstValidatedAt` to `usedAt`). This
   * schema declared `firstUsedAt`, which no producer emitted and no consumer
   * read — the contract was simply untrue. Corrected rather than left alongside
   * a second, right one (Rule 7a: "it exists but is wrong -> fix it where it
   * lives"). The admin order-detail page has always read `usedAt`.
   */
  usedAt: z.string().optional(),
});

export type AdminOrderTicket = z.infer<typeof AdminOrderTicketSchema>;

export const AdminOrderTimelineEntrySchema = z.object({
  id: z.string(),
  status: AdminOrderSummarySchema.shape.status,
  timestamp: z.string(),
  actor: z.string(),
  note: z.string().optional(),
});

export type AdminOrderTimelineEntry = z.infer<typeof AdminOrderTimelineEntrySchema>;

/**
 * An admin note as it is actually stored and served.
 *
 * This previously declared `{ id, content, … }`, which nothing ever produced:
 * `admin-orders.service.ts` `addNote` appends `{ text, createdBy, createdAt }` to
 * `Order.metadata.notes` and the detail endpoint returns that array verbatim.
 * There is no id — the notes are a JSON array, not rows.
 *
 * The drift survived because the controller validated the detail response against
 * a second copy of the schema that typed notes as `z.array(z.unknown())`. The
 * add-note response, {@link AdminAddNoteDataSchema}, had the shape right all along.
 */
export const AdminOrderNoteSchema = z.object({
  text: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
});

export type AdminOrderNote = z.infer<typeof AdminOrderNoteSchema>;

export const AdminOrderAuditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  actor: z.string(),
  timestamp: z.string(),
  details: z.string().optional(),
});

export type AdminOrderAuditEntry = z.infer<typeof AdminOrderAuditEntrySchema>;

export const AdminCustomerDetailsSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string(),
});

export type AdminCustomerDetails = z.infer<typeof AdminCustomerDetailsSchema>;

export const AdminPaymentDetailsSchema = z.object({
  method: z.string(),
  transactionId: z.string(),
  paidAt: z.string().nullable(),
  /**
   * How much has actually been settled, in IDR — not what the order costs.
   *
   * Named for what it is because the previous `amount` was neither: it was
   * `Order.totalIdr`, so a group booking that had paid only its deposit reported
   * the full total as paid. `AdminOrderDetail.total` remains the amount owed;
   * these two are equal for a fully-paid individual order and for nothing else.
   *
   * Zero, not null, when nothing has been paid: a payment card that has taken no
   * money has taken zero, and `paidAt` already carries "has this settled at all".
   */
  amountPaid: z.number(),
  /**
   * Which gateway issued `transactionId`, projected from `PaymentSucceeded.gateway`
   * onto `Order.gateway` (#737, #740).
   *
   * Typed from the existing {@link PaymentGatewaySchema} rather than `z.string()`
   * so the HTTP vocabulary cannot drift from the Kafka one — they are the same
   * four lowercase values, and a test asserts they stay in step.
   *
   * Nullable, not optional: orders fulfilled before `Order.gateway` existed have
   * no gateway, and the API says so explicitly rather than omitting the key and
   * making the frontend guess whether it was absent or unknown.
   */
  gateway: PaymentGatewaySchema.nullable(),
  /**
   * payment-service's own `Payment.id`, projected onto `Order.paymentId` by the
   * payment-confirmation saga. This is the only identifier here that addresses
   * `/payments/:id` — hence a field of its own rather than reusing
   * `transactionId`.
   *
   * The two are NOT interchangeable even though they are equal for web orders.
   * `transactionId` resolves to `Payment.gatewayReference`, which on the Snap
   * path happens to hold the Midtrans order_id (= `Payment.id`), but on the
   * kiosk/EDC paths holds the acquirer's own reference
   * (`payments-kiosk.service.ts:128,176`). Linking on `transactionId` would
   * therefore 404 for exactly the channels that are hardest to test.
   *
   * Nullable: unpaid orders have no payment yet, and the UI must render the id
   * as plain text rather than a dead link.
   */
  paymentId: z.string().nullable(),
});

export type AdminPaymentDetails = z.infer<typeof AdminPaymentDetailsSchema>;

export const AdminGroupAddonLineSchema = z.object({
  attractionTicketTypeId: z.string(),
  attractionName: z.string().optional(),
  quantity: z.number().int(),
  unitPriceIdr: z.number().optional(),
  subtotalIdr: z.number().optional(),
});

export type AdminGroupAddonLine = z.infer<typeof AdminGroupAddonLineSchema>;

export const AdminGroupBookingInfoSchema = z.object({
  bookingNumber: z.string(),
  groupName: z.string(),
  groupType: z.string(),
  status: z.string(),
  paymentMode: z.string(),
  expectedMembers: z.number().int(),
  depositPaidAt: z.string().nullable(),
  balancePaidAt: z.string().nullable(),
  depositAmountIdr: z.number(),
  totalIdr: z.number(),
  addonLines: z.array(AdminGroupAddonLineSchema),
});

export type AdminGroupBookingInfo = z.infer<typeof AdminGroupBookingInfoSchema>;

export const AdminOrderDetailSchema = AdminOrderSummarySchema.extend({
  customer: AdminCustomerDetailsSchema,
  payment: AdminPaymentDetailsSchema,
  /**
   * How the order was placed — `web`, `kiosk`, `pos`, `reseller`, … Persisted as
   * `Order.sourceChannel` and already returned by `admin-orders.service.findOne`;
   * it was simply never declared here, so no frontend could reach it through
   * `z.infer`. Declared on the detail rather than the summary because the list
   * endpoint does not return it.
   *
   * Free-form: unlike `gateway` this vocabulary is ticketing's own and is written
   * by several creation paths, so there is no shared enum to import (Rule 7a).
   */
  sourceChannel: z.string().optional(),
  tickets: z.array(AdminOrderTicketSchema),
  timeline: z.array(AdminOrderTimelineEntrySchema),
  notes: z.array(AdminOrderNoteSchema),
  auditLog: z.array(AdminOrderAuditEntrySchema),
  groupBooking: AdminGroupBookingInfoSchema.nullable().optional(),
  parentOrderId: z.string().nullable().optional(),
  parentOrderNumber: z.string().nullable().optional(),
});

export type AdminOrderDetail = z.infer<typeof AdminOrderDetailSchema>;

export const AddOrderNoteSchema = z.object({
  content: z.string().min(1).max(1000),
});

export type AddOrderNoteInput = z.infer<typeof AddOrderNoteSchema>;
